import { CommonModule } from '@angular/common';
import { Component, OnInit, Signal, TemplateRef, ViewChild, computed, effect, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';
import { OrgUserRoles, ProjectUserRoles, ProjectUsers, OrganizationProjects, Project, Organization, OrganizationUsers } from '@models';
import { WorkspaceFacade, UserFacade } from '@state';
import { ModalService, NotificationsService, OrganizationInviteService, OrganizationProjectsService, OrganizationUsersService, ProjectInviteService, ProjectService, ProjectUsersService, ToastService } from '@services';
import { OrganizationInvite, ProjectInvite } from '@models';
import { Operator, appstraxUsers } from '@appstrax/services/auth';
import { lookupUserEmails } from '../../utils/user-email.util';
import { resolveActiveOrganizationId } from '../../utils/workspace-scope.util';
import { Operator as DbOperator } from '@appstrax/services/database';

@Component({
  selector: 'app-users',
  templateUrl: './users.page.html',
  styleUrls: ['./users.page.scss'],
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule],
})
export class UsersPage implements OnInit {
  private userStore = inject(UserFacade);
  private projectUsersService = inject(ProjectUsersService);
  private orgUsersService = inject(OrganizationUsersService);
  private orgProjectsService = inject(OrganizationProjectsService);
  private projectService = inject(ProjectService);
  search = signal<string>('');
  projectRoleFilter = signal<string>('');
  orgRoleFilter = signal<string>('');
  loading = signal<boolean>(false);

  roles = Object.values(ProjectUserRoles);
  orgRoles = Object.values(OrgUserRoles);

  inviteToEmail = signal<string>('');
  emailLookup = signal<{ status: 'idle' | 'checking' | 'found' | 'not_found' | 'invalid' | 'error'; message?: string }>({
    status: 'idle',
  });
  private resolvedInviteUserId: string | null = null;
  private emailLookupTimer: any = null;
  private emailLookupSeq = 0;
  inviteProjectRole = signal<string>(ProjectUserRoles.MEMBER);
  inviteOrgRole = signal<string>(OrgUserRoles.USER);
  inviting = signal<boolean>(false);

  // Invite UI mode
  inviteMode = signal<'project' | 'organization'>('project');

  // Organization bulk invite config
  bulkOrgId = signal<string>('');
  bulkProjectRole = signal<string>(ProjectUserRoles.MEMBER);
  bulkProjects = signal<Project[]>([]);
  bulkSelectedProjectIds = signal<string[]>([]);
  bulkLoadingProjects = signal<boolean>(false);
  loadingInvites = signal<boolean>(false);
  outgoingProjectInvites = signal<ProjectInvite[]>([]);
  outgoingOrganizationInvites = signal<OrganizationInvite[]>([]);

  // Cache for mapping userId -> email (for display/search)
  private userEmailById = signal<Record<string, string>>({});

  activeProjectId = computed(() => this.store.activeProjectId());
  activeProjectLabel = computed(() => {
    const pid = this.activeProjectId();
    if (!pid) return 'No project selected';
    const proj = this.store.projects()?.find((p: any) => p.id === pid) ?? null;
    const name = (proj as any)?.name ?? (proj as any)?.title ?? null;
    return name ? `${name}` : 'No project selected';
  });
  currentUserId = computed(() => this.userStore.current()?.id ?? '');
  organizations!: Signal<Organization[]>;
  activeOrganizationId = computed(() =>
    resolveActiveOrganizationId({
      selectedOrganizationId: this.store.selectedOrganizationId(),
      activeProjectId: this.activeProjectId(),
      orgProjects: this.store.orgProjects(),
      organizations: this.store.organizations(),
    })
  );
  activeOrganizationLabel = computed(() => {
    const oid = this.activeOrganizationId();
    if (!oid) return 'No organization selected';
    const org = this.store.organizations()?.find((o: any) => o.id === oid) ?? null;
    const name = (org as any)?.name ?? (org as any)?.title ?? null;
    return name ? `${name}` : 'No organization selected';
  });

  projectUsers = computed(() => {
    const pid = this.activeProjectId();
    if (!pid) return [];
    return this.store.projectUsers().filter((pu) => pu.projectId === pid);
  });

  organizationUsers = computed(() => {
    const orgId = this.activeOrganizationId();
    if (!orgId) return [];
    return this.store.orgUsers().filter((ou) => ou.organizationId === orgId);
  });

  filteredProjectUsers = computed(() => {
    const q = (this.search() || '').toLowerCase().trim();
    const role = (this.projectRoleFilter() || '').trim();
    const emails = this.userEmailById();
    return this.projectUsers().filter((u) => {
      if (role && u.role !== (role as any)) return false;
      if (!q) return true;
      const email = emails[u.userId] ?? '';
      return [email, u.role].some((v) => (v || '').toString().toLowerCase().includes(q));
    });
  });

  filteredOrganizationUsers = computed(() => {
    const q = (this.search() || '').toLowerCase().trim();
    const role = (this.orgRoleFilter() || '').trim();
    const emails = this.userEmailById();
    return this.organizationUsers().filter((u) => {
      if (role && (u as any).role !== (role as any)) return false;
      if (!q) return true;
      const email = emails[u.userId] ?? '';
      return [email, (u as any).role].some((v) => (v || '').toString().toLowerCase().includes(q));
    });
  });

  @ViewChild('confirmRemoveOrgUserModal') confirmRemoveOrgUserModal!: TemplateRef<any>;
  @ViewChild('confirmRemoveProjectUserModal') confirmRemoveProjectUserModal!: TemplateRef<any>;

  pendingOrgRemove: OrganizationUsers | null = null;
  pendingOrgRemoveProjects: Project[] = [];
  pendingProjectRemove: ProjectUsers | null = null;
  removingUser = false;

  constructor(
    private store: WorkspaceFacade,
    private toast: ToastService,
    private modal: ModalService,
    private notifications: NotificationsService,
    private projInvites: ProjectInviteService,
    private orgInvites: OrganizationInviteService,
  ) {
    this.organizations = this.store.organizations;
    effect(() => {
      // Refresh when the active project changes
      const pid = this.activeProjectId();
      if (!pid) return;
      void this.refresh();
      void this.refreshOutgoingInvites();
    });
    effect(() => {
      // Refresh when org changes (members + org invites table)
      const orgId = this.activeOrganizationId();
      if (!orgId) return;
      void this.refresh();
      void this.refreshOutgoingInvites();
    });

    // Smart email field: debounce lookup and show status.
    effect(() => {
      const raw = (this.inviteToEmail() || '').trim();
      const email = raw.toLowerCase();

      // Reset state
      this.resolvedInviteUserId = null;
      if (this.emailLookupTimer) {
        clearTimeout(this.emailLookupTimer);
        this.emailLookupTimer = null;
      }

      if (!email) {
        this.emailLookup.set({ status: 'idle' });
        return;
      }

      // Minimal “is email-ish” check to avoid hammering API mid-typing.
      const isProbablyEmail = email.includes('@') && email.includes('.') && !email.endsWith('@') && !email.endsWith('.');
      if (!isProbablyEmail) {
        this.emailLookup.set({ status: 'invalid', message: 'Enter a valid email' });
        return;
      }

      this.emailLookup.set({ status: 'checking' });
      const seq = ++this.emailLookupSeq;
      this.emailLookupTimer = setTimeout(async () => {
        try {
          const id = await this.resolveUserIdByEmail(email);
          // Ignore stale responses.
          if (seq !== this.emailLookupSeq) return;
          if (!id) {
            this.resolvedInviteUserId = null;
            this.emailLookup.set({ status: 'not_found', message: 'No user found' });
            return;
          }
          this.resolvedInviteUserId = id;
          this.emailLookup.set({ status: 'found' });
        } catch (e: any) {
          if (seq !== this.emailLookupSeq) return;
          this.resolvedInviteUserId = null;
          this.emailLookup.set({ status: 'error', message: e?.message ?? 'Lookup failed' });
        }
      }, 450);
    });
  }

  async ngOnInit(): Promise<void> {
    await this.refresh();
    await this.refreshOutgoingInvites();
  }

  async refresh(): Promise<void> {
    const pid = this.activeProjectId();
    const orgId = this.activeOrganizationId();
    if (!pid && !orgId) return;
    this.loading.set(true);
    try {
      const [projectRes, orgRes] = await Promise.all([
        pid
          ? this.projectUsersService.find({ where: { projectId: pid } as any })
          : Promise.resolve(null),
        orgId
          ? this.orgUsersService.find({ where: { organizationId: orgId } as any })
          : Promise.resolve(null),
      ]);
      if (projectRes) this.store.upsertEntities({ projectUsers: projectRes.data ?? [] });
      if (orgRes) this.store.upsertEntities({ orgUsers: orgRes.data ?? [] });

      const ids = Array.from(
        new Set(
          [
            ...this.projectUsers().map((u) => u.userId),
            ...this.organizationUsers().map((u) => u.userId),
          ].filter(Boolean)
        )
      );
      await this.hydrateUserEmails(ids);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load users', 'Users');
    } finally {
      this.loading.set(false);
    }
  }

  onSearchChange(value: string) {
    this.search.set(value ?? '');
  }

  onProjectRoleFilterChange(value: string) {
    this.projectRoleFilter.set(value ?? '');
  }

  onOrgRoleFilterChange(value: string) {
    this.orgRoleFilter.set(value ?? '');
  }

  resetFilters() {
    this.search.set('');
    this.projectRoleFilter.set('');
    this.orgRoleFilter.set('');
  }

  async updateRole(link: ProjectUsers, role: string) {
    try {
      if (link.userId === this.currentUserId()) return;
      const updated = { ...(link as any), role } as ProjectUsers;
      const saved = await this.projectUsersService.save(updated);
      this.store.upsertEntities({ projectUsers: [saved] });
      this.toast.success('Role updated', 'Users');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to update role', 'Users');
    }
  }

  async remove(link: ProjectUsers) {
    try {
      if (link.userId === this.currentUserId()) return;
      await this.projectUsersService.delete(link.id);
      this.store.removeEntities({ projectUsers: [link] });
      this.toast.success('Removed from project', 'Users');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to remove user', 'Users');
    }
  }

  async updateOrgRole(link: OrganizationUsers, role: string) {
    try {
      if (link.userId === this.currentUserId()) return;
      const updated = { ...(link as any), role } as OrganizationUsers;
      const saved = await this.orgUsersService.save(updated);
      this.store.upsertEntities({ orgUsers: [saved] });
      this.toast.success('Org role updated', 'Users');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to update org role', 'Users');
    }
  }

  async removeFromOrg(link: OrganizationUsers) {
    try {
      if (link.userId === this.currentUserId()) return;
      this.pendingOrgRemove = link;
      this.pendingOrgRemoveProjects = [];

      // Precompute impacted projects for the confirm modal.
      try {
        const orgId = link.organizationId;
        if (orgId) {
          const res = await this.orgProjectsService.find({ where: { organizationId: orgId } as any });
          const mappings = (res.data ?? []) as OrganizationProjects[];
          const projectIds = Array.from(new Set(mappings.map((m: any) => m.projectId).filter(Boolean)));
          if (projectIds.length) {
            const projRes = await this.projectService.find({ where: { id: { [DbOperator.IN]: projectIds } } as any });
            const projects = (projRes.data ?? []).slice();
            projects.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
            this.pendingOrgRemoveProjects = projects as any;
          }
        }
      } catch {
        // Silent: modal can still open without the project list
      }

      this.modal.open(this.confirmRemoveOrgUserModal, { centered: true, backdrop: 'static', keyboard: true });
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to remove user from organization', 'Users');
    }
  }

  openRemoveFromProject(link: ProjectUsers) {
    if (link.userId === this.currentUserId()) return;
    this.pendingProjectRemove = link;
    this.modal.open(this.confirmRemoveProjectUserModal, { centered: true, backdrop: 'static', keyboard: true });
  }

  closeConfirmRemoveUserModal() {
    if (this.removingUser) return;
    this.pendingOrgRemove = null;
    this.pendingOrgRemoveProjects = [];
    this.pendingProjectRemove = null;
    this.modal.dismiss();
  }

  async confirmRemoveFromProject() {
    const link = this.pendingProjectRemove;
    if (!link) return;
    if (link.userId === this.currentUserId()) return;
    if (this.removingUser) return;
    this.removingUser = true;
    try {
      const removedProjectId = (link as any).projectId;
      const targetUserId = (link as any).userId;
      await this.projectUsersService.delete(link.id);
      this.store.removeEntities({ projectUsers: [link] });

      // If this was the user's last project in the organization, also remove org membership.
      // Organization membership is treated as derived access (no org-only access).
      try {
        const mappingsRes = await this.orgProjectsService.find({ where: { projectId: removedProjectId } as any });
        const orgIdsForRemovedProject = Array.from(
          new Set(
            (mappingsRes.data ?? []).map((m: any) => String(m?.organizationId ?? '').trim()).filter(Boolean)
          )
        );

        if (orgIdsForRemovedProject.length > 0) {
          // Fetch the user's remaining project memberships (post-delete).
          const remainingRes = await this.projectUsersService.find({ where: { userId: targetUserId } as any });
          const remainingProjectIds = Array.from(
            new Set((remainingRes.data ?? []).map((pu: any) => pu.projectId).filter(Boolean))
          );

          let orgIdsWithRemainingProjects = new Set<string>();
          if (remainingProjectIds.length > 0) {
            const res = await this.orgProjectsService.find({
              where: { projectId: { [DbOperator.IN]: remainingProjectIds } } as any,
            });
            orgIdsWithRemainingProjects = new Set(
              (res.data ?? []).map((m: any) => String(m?.organizationId ?? '').trim()).filter(Boolean)
            );
          }

          const orgIdsToRemove = orgIdsForRemovedProject.filter((orgId) => !orgIdsWithRemainingProjects.has(orgId));
          for (const orgId of orgIdsToRemove) {
            try {
              const res = await this.orgUsersService.find({
                where: { organizationId: orgId, userId: targetUserId } as any,
              });
              const orgLink = res.data?.[0] ?? null;
              if (orgLink) {
                await this.orgUsersService.delete(orgLink.id);
                this.store.removeEntities({ orgUsers: [orgLink] });
              }
            } catch {}
          }
        }
      } catch {
        // Best-effort only: project removal should still succeed even if cleanup fails.
      }

      this.toast.success('Removed from project', 'Users');
      this.modal.close();
      this.pendingProjectRemove = null;
      await this.refresh();
      await this.store.refreshWorkspaceData();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to remove user from project', 'Users');
    } finally {
      this.removingUser = false;
    }
  }

  async confirmRemoveFromOrganization() {
    const link = this.pendingOrgRemove;
    if (!link) return;
    if (link.userId === this.currentUserId()) return;
    if (this.removingUser) return;
    this.removingUser = true;
    try {
      const orgId = link.organizationId;
      const targetUserId = link.userId;

      // Find all projects in this organization.
      const mappingsRes = await this.orgProjectsService.find({ where: { organizationId: orgId } as any });
      const projectIds = Array.from(
        new Set((mappingsRes.data ?? []).map((op: any) => op.projectId).filter(Boolean))
      );

      // Load this user's project memberships, then delete only those for projects in this org.
      const userLinksRes = await this.projectUsersService.find({ where: { userId: targetUserId } as any });
      const projectLinksToRemove = (userLinksRes.data ?? []).filter((pu: any) => projectIds.includes(pu.projectId));

      for (const pu of projectLinksToRemove) {
        await this.projectUsersService.delete(pu.id);
      }

      // Finally remove org membership.
      await this.orgUsersService.delete(link.id);
      this.store.removeEntities({ projectUsers: projectLinksToRemove, orgUsers: [link] });

      this.toast.success('Removed from organization and its projects', 'Users');
      this.modal.close();
      this.pendingOrgRemove = null;

      // Refresh overall app data so selectors update.
      await this.store.refreshWorkspaceData();
      await this.refresh();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to remove user from organization', 'Users');
    } finally {
      this.removingUser = false;
    }
  }

  async refreshOutgoingInvites(): Promise<void> {
    const fromUserId = this.userStore.current()?.id ?? '';
    const projectId = this.activeProjectId();
    const organizationId = this.activeOrganizationId();
    if (!fromUserId || (!projectId && !organizationId)) return;

    this.loadingInvites.set(true);
    try {
      const [projRes, orgRes] = await Promise.all([
        projectId
          ? this.projInvites.find({ where: { fromUserId, projectId, status: 'pending' } as any })
          : Promise.resolve({ data: [] } as any),
        organizationId
          ? this.orgInvites.find({ where: { fromUserId, organizationId, status: 'pending' } as any })
          : Promise.resolve({ data: [] } as any),
      ]);
      this.outgoingProjectInvites.set((projRes.data ?? []) as ProjectInvite[]);
      this.outgoingOrganizationInvites.set((orgRes.data ?? []) as OrganizationInvite[]);

      const toIds = Array.from(
        new Set(
          [
            ...((projRes.data ?? []) as any[]).map((x) => x?.toUserId),
            ...((orgRes.data ?? []) as any[]).map((x) => x?.toUserId),
          ].filter(Boolean)
        )
      );
      await this.hydrateUserEmails(toIds);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load outgoing invites', 'Users');
      this.outgoingProjectInvites.set([]);
      this.outgoingOrganizationInvites.set([]);
    } finally {
      this.loadingInvites.set(false);
    }
  }

  userEmail(userId: string | undefined | null): string {
    if (!userId) return '';
    return this.userEmailById()[userId] ?? '';
  }

  private async hydrateUserEmails(userIds: string[]): Promise<void> {
    this.userEmailById.set(await lookupUserEmails(userIds, this.userEmailById()));
  }

  private async resolveUserIdByEmail(email: string): Promise<string | null> {
    const raw = (email || '').trim();
    const e = raw.toLowerCase();
    if (!e) return null;

    // Auth user search expects operators (per docs), so do EQUAL then fallback to LIKE.
    const tryQuery = async (where: any) => {
      const res: any = await appstraxUsers.find({ where, order: { email: 'ASC' } as any, offset: 0, limit: 5 });
      return (res?.data ?? []) as any[];
    };

    let list: any[] = [];
    try {
      list = await tryQuery({ email: { [Operator.EQUAL]: e } });
    } catch {
      // ignore, try LIKE
    }
    if (!list.length) {
      try {
        list = await tryQuery({ email: { [Operator.LIKE]: e } });
      } catch {
        list = [];
      }
    }

    // Prefer exact match if multiple results from LIKE.
    const match = list.find((u) => String(u?.email ?? '').trim().toLowerCase() === e) ?? list[0] ?? null;
    const id = String(match?.id ?? '').trim();
    if (!id) return null;

    const next = { ...this.userEmailById() };
    next[id] = String(match?.email ?? raw).trim();
    this.userEmailById.set(next);
    return id;
  }

  async cancelProjectInvite(invite: ProjectInvite) {
    try {
      await this.projInvites.delete(invite.id);
      await this.refreshOutgoingInvites();
      this.toast.success('Invite cancelled', 'Users');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to cancel invite', 'Users');
    }
  }

  async cancelOrganizationInvite(invite: OrganizationInvite) {
    try {
      await this.orgInvites.delete(invite.id);
      await this.refreshOutgoingInvites();
      this.toast.success('Invite cancelled', 'Users');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to cancel invite', 'Users');
    }
  }

  private async sendInviteToResolvedUser(
    toEmail: string,
    scopeError: string | null,
    send: (toUserId: string) => Promise<void>,
    successMessage: string,
    failureMessage: string,
  ): Promise<void> {
    if (scopeError) {
      this.toast.error(scopeError, 'Users');
      return;
    }
    if (this.inviting()) return;
    this.inviting.set(true);
    try {
      const toUserId = this.resolvedInviteUserId ?? (await this.resolveUserIdByEmail(toEmail));
      if (!toUserId) {
        this.toast.error('No user found for that email address', 'Users');
        return;
      }
      await send(toUserId);
      this.toast.success(successMessage, 'Users');
      this.inviteToEmail.set('');
      await this.refreshOutgoingInvites();
    } catch (e: any) {
      this.toast.error(e?.message ?? failureMessage, 'Users');
    } finally {
      this.inviting.set(false);
    }
  }

  async inviteToProject(): Promise<void> {
    const toEmail = (this.inviteToEmail() || '').trim();
    const pid = this.activeProjectId();
    await this.sendInviteToResolvedUser(
      toEmail,
      !toEmail || !pid ? 'Target email and project must be selected' : null,
      async (toUserId) => {
        await this.notifications.sendProjectInvite(toUserId, pid!, this.inviteProjectRole());
      },
      'Invite sent',
      'Failed to send invite',
    );
  }

  // UI actions for header buttons
  openProjectInvite() {
    this.inviteMode.set('project');
  }

  async openOrganizationInvite() {
    this.inviteMode.set('organization');
    // Default to active org (if any)
    const orgId = this.activeOrganizationId();
    if (orgId && !this.bulkOrgId()) {
      this.bulkOrgId.set(orgId);
      await this.loadOrgProjects(orgId);
    }
  }

  async onBulkOrgChange(orgId: string) {
    this.bulkOrgId.set(orgId);
    await this.loadOrgProjects(orgId);
  }

  private async loadOrgProjects(orgId: string) {
    if (!orgId) {
      this.bulkProjects.set([]);
      this.bulkSelectedProjectIds.set([]);
      return;
    }
    this.bulkLoadingProjects.set(true);
    try {
      // Load org->project mappings from DB, then fetch the projects themselves.
      const res = await this.orgProjectsService.find({ where: { organizationId: orgId } as any });
      const mappings = (res.data ?? []) as OrganizationProjects[];
      const projectIds = Array.from(new Set(mappings.map((m) => (m as any).projectId).filter(Boolean)));
      let projects: Project[] = [];
      if (projectIds.length) {
        const projRes = await this.projectService.find({ where: { id: { [DbOperator.IN]: projectIds } } as any });
        projects = (projRes.data ?? []).slice();
      }
      projects.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      this.bulkProjects.set(projects as any);
      // Default select all projects in the org
      this.bulkSelectedProjectIds.set(projectIds);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load organization projects', 'Users');
      this.bulkProjects.set([]);
      this.bulkSelectedProjectIds.set([]);
    } finally {
      this.bulkLoadingProjects.set(false);
    }
  }

  toggleBulkProject(projectId: string) {
    const set = new Set(this.bulkSelectedProjectIds());
    if (set.has(projectId)) set.delete(projectId);
    else set.add(projectId);
    this.bulkSelectedProjectIds.set(Array.from(set));
  }

  selectAllBulkProjects() {
    this.bulkSelectedProjectIds.set(this.bulkProjects().map((p: any) => p.id));
  }

  clearBulkProjects() {
    this.bulkSelectedProjectIds.set([]);
  }

  async sendBulkOrganizationInvite(): Promise<void> {
    const toEmail = (this.inviteToEmail() || '').trim();
    const orgId = (this.bulkOrgId() || '').trim();
    const projectIds = this.bulkSelectedProjectIds();
    await this.sendInviteToResolvedUser(
      toEmail,
      !toEmail || !orgId ? 'Target email and organization must be selected' : null,
      async (toUserId) => {
        await this.notifications.sendOrganizationBulkInvite(
          toUserId,
          orgId,
          this.inviteOrgRole(),
          projectIds,
          this.bulkProjectRole(),
        );
      },
      'Bulk invite sent',
      'Failed to send bulk invite',
    );
  }
}
